<template>
    <div class=" flex justify-center items-center pt-8">
        <div class="w-full  bg-white rounded-lg shadow-md">
            <DataTable  selectionMode="single" filterDisplay="row" v-model:filters="filters" ref="dataTable"
                v-model:editingRows="editingRows" :value="sites" editMode="cell" dataKey="id" 
                @cell-edit-complete="onCellEditComplete" scrollable :scrollHeight="'80vh'">
                <Column frozen alignFrozen="left" header="Publisher" filterField="username" :showFilterMenu="false"
                     :headerStyle="headerStyles">
                    <template v-if="isLoading" #body>
                        <Skeleton></Skeleton>
                    </template>
                    <template  v-else #body="slotProps">
                        <!-- Create a router link using the data in the slotProps -->
                        <router-link :to="`/users/${slotProps.data.userId}`" class="clickable-link">
                            {{ slotProps.data.username }}
                        </router-link>
                    </template>
                    <template #filter="{ filterModel, filterCallback }">
                        <MultiSelect v-model="filterModel.value" @change="filterCallback()" :options="usernames"
                            placeholder="Any" style="max-width: 8rem" :maxSelectedLabels="1">
                            <template #option="slotProps">
                                <div class="flex items-center gap-2">
                                    <span>{{ slotProps.option }}</span>
                                </div>
                            </template>
                        </MultiSelect>
                    </template>
                </Column>
                <Column field="name" header="Name" :headerStyle="headerStyles">
                    <template v-if="isLoading" #body>
                        <Skeleton></Skeleton>
                    </template>
                </Column>
                <Column field="type" header="Type" :showFilterMenu="false" :headerStyle="headerStyles">
                    <template v-if="isLoading" #body>
                        <Skeleton></Skeleton>
                    </template>
                    <template v-else #body="slotProps">
                        <Tag :value="slotProps.data.type" :severity="Utils.getSiteTypeSeverity(slotProps.data.type)" />
                    </template>
                    <template #filter="{ filterModel, filterCallback }">
                        <MultiSelect v-model="filterModel.value" @change="filterCallback()" :options="siteTypes"
                            optionLabel="name" optionValue="code" placeholder="Any" style="min-width: 8rem;"
                            :maxSelectedLabels="1" selectedItemsLabel="{0} types">
                            <template #option="slotProps">
                                <div class="flex items-center gap-2">
                                    <span>{{ slotProps.option.name }}</span>
                                </div>
                            </template>
                        </MultiSelect>
                    </template>
                </Column>
                <Column field="createdAt" header="Created" :headerStyle="headerStyles">
                    <template v-if="isLoading" #body>
                        <Skeleton></Skeleton>
                    </template>
                </Column>
                <Column field="url" header="Domain" :headerStyle="headerStyles">
                    <template v-if="isLoading" #body>
                        <Skeleton></Skeleton>
                    </template>
                    <template v-else #body="slotProps">
                        <a class="clickable-link" :href="Utils.formatUrl(slotProps.data.url)" target="_blank"
                            rel="noopener noreferrer">{{ slotProps.data.url
                            }}</a>
                    </template>
                </Column>
                <Column field="country" header="Country" :headerStyle="headerStyles">
                    <template v-if="isLoading" #body>
                        <Skeleton></Skeleton>
                    </template>
                    <template v-else #body="{ data, field }">
                        <!-- Display the country name based on the country code -->
                        <span>{{ getCountryName(data[field]) }}</span>
                        <span :class="`fi fi-${data[field].toLowerCase()}`" class="ml-2"></span>
                    </template>
                </Column>
                <Column field="status" header="Status" :showFilterMenu="false" :headerStyle="headerStyles">
                    <template v-if="!isLoading" #editor="{ data, field }">
                        <Select v-model="data[field]" :options="statuses" optionLabel="status" optionValue="status"
                            fluid>
                            <template #option="slotProps">
                                <Tag :value="slotProps.option.status"
                                    :severity="Utils.getSiteStatusSeverity(slotProps.option.status)" />
                            </template>
                        </Select>
                    </template>
                    <template v-if="isLoading" #body>
                        <Skeleton></Skeleton>
                    </template>
                    <template v-else #body="slotProps">
                        <Tag :value="slotProps.data.status"
                            :severity="Utils.getSiteStatusSeverity(slotProps.data.status)" class="cursor-pointer" />
                    </template>
                    
                    <template #filter="{ filterModel, filterCallback }">
                        <MultiSelect style="max-width: 10rem" v-model="filterModel.value" placeholder="Any"
                            @change="filterCallback()" :options="statuses" optionLabel="status" optionValue="status"
                            :maxSelectedLabels="1" selectedItemsLabel="{0} statuses" fluid>
                            <template #option="slotProps">
                                <Tag :value="slotProps.option.status"
                                    :severity="Utils.getSiteStatusSeverity(slotProps.option.status)" />
                            </template>
                        </MultiSelect>

                    </template>
                </Column>
                
            </DataTable>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, watch } from "vue";
import { FilterMatchMode } from '@primevue/core/api';
import { useToast } from "primevue/usetoast";
import SitesService from '@/services/SitesService';
import { useStore } from 'vuex';
import Utils from '@/utils';
import { useRoute } from "vue-router";
import { headerStyles, nestedHeaderStyles } from '@/global/headerStyles';
import countries from '@/utils/countries';
import { getCountryName } from '@/utils/countries';
import { getSiteTypes } from '@/utils/siteTypes';

const store = useStore();
const route = useRoute();

async function fetchSites() {
    try {
        await SitesService.getAll().then((data) => {
            console.log(data);
            sites.value = data.data;
        });
    } catch (err) {
        console.error(err);
    } finally {
        isLoading.value = false;
    }
}

const statuses = ref([{ status: 'pending approval' }, { status: 'approved' }, { status: 'rejected' }]);
// Function to populate usernames from sites
const populateUsernames = (sites) => {
    usernames.value = [...new Set(sites.map(site => site.username))];
};

const toast = useToast();
const isLoading = ref(true);

//datatable 
const dataTable = ref(null); // Reference to DataTable
const filters = ref({
    username: { matchMode: FilterMatchMode.IN },
    type: { matchMode: FilterMatchMode.IN },
    status: { matchMode: FilterMatchMode.IN }
});


const siteTypes = ref(getSiteTypes());

const editingRows = ref([]);
const sites = ref(new Array(10));
const usernames = ref([]);

const onCellEditComplete = async (event) => {
    let { newValue, oldValue, data } = event;
    if (newValue !== oldValue) {
        try {
            const response = await SitesService.updateStatus(data.id, newValue);
            if (response.status === 200) {
                sites.value.find(site => site.id == data.id).status = newValue;
            }
        } catch (err) {
            console.error(err);
        }
    }
}

onMounted(async () => {
    await fetchSites();
});

watch(filters, (newFilter) => {
    console.log('Filters:', newFilter);
});

watch(sites, (newSites) => {
    populateUsernames(newSites);
    const queryStatus = route.query.status;
    console.log('Query Status:', queryStatus);
    if (queryStatus && queryStatus === 'pendingapproval') {
        // Set the status filter based on query param
        filters.value.status.value = ['pending approval'];
    }
});
</script>

<style scoped>
.clickable-link {
    color: var(--color-primary);
    text-decoration: underline;
}
</style>